import { useDispatch, useSelector } from 'react-redux';
import {
  activeContactSelector,
  getActiveContactID,
  getActiveInteractionID,
  updateAppSpaceTab,
  updateContactIntents,
  updateTwitterAgentReply,
} from '../../../../ccf-inbox/ccf-inbox.slice';
import { useCallback, useEffect } from 'react';
import CcfCopilotNBR from '../ccf-copilot-nbr/ccf-copilot-nbr';
import { customCopilotTemplate, copilotFlow } from '../mock-data/custom-flow';
import {
  getcopilotFlowMsgIndex,
  getIsAssetsAddedToCopilotSuggestionState,
  setIsAssetsAddedToCopilotSuggestion,
  updateFlowMsgIndex,
} from '../+state/ccf-copilot.slice';
import { AppSpace } from 'libs/react/ui-components/src/enums/app-space-tabs';
import {
  dispositionInteractionActions,
  getIsDispositionOpen,
} from '../../../../ccf-disposition/ccf-disposition-slice.tsx';
import { ApiResponse } from '../../ccf-copilot-api';
import { processApiResponse } from '../../ccf-copilot-util';
import {
  selectRunningSummary,
  setIsRequestProcessing,
} from '../+state/ccf-live-copilot.slice';
import { DigitalChannelType, MediaType } from '@nice-ccf/core-sdk';
import { useTranslator } from '@nice-ccf/ui-controls';

/* eslint-disable-next-line */
export interface CcfCopilotChatSuggestionCardProps {}

/**
 * CcfCopilotChatSuggestionCard - used to display Copilot card in chat text editor
 * @param props -?-CcfCopilotChatSuggestionCardProps
 * @example <CcfCopilotChatSuggestionCard />
 */
export function CcfCopilotChatSuggestionCard(
  props: CcfCopilotChatSuggestionCardProps
) {
  const [translate] = useTranslator();
  const flowMsgIndex = useSelector(getcopilotFlowMsgIndex);
  const activeContact = useSelector(activeContactSelector);
  const activeInteractionID: string = useSelector(getActiveInteractionID) || '';
  let copilotFlowJson =
    activeContact?.customerChatReplyJsonData?.length > 0
      ? activeContact?.customerChatReplyJsonData
      : customCopilotTemplate?.copilotAgentAndCustomerChatData;
  copilotFlowJson = copilotFlowJson.filter(
    (item: any) => item.messageFrom !== 'elevatedEmail'
  );
  copilotFlowJson = copilotFlowJson.filter(
    (item: any) => item.messageFrom !== 'elevatedSMS'
  );
  const isAssetsAddedToCopilotSuggestion = useSelector(
    getIsAssetsAddedToCopilotSuggestionState
  );
  const activeContactId = useSelector(getActiveContactID);
  const rtSummary = useSelector(selectRunningSummary(activeContactId || ''));
  const suggestionsCardList: copilotFlow = copilotFlowJson[flowMsgIndex];
  const isDispositionOpen = useSelector(getIsDispositionOpen);
  const { toggleDispositionCard } = dispositionInteractionActions;
  const dispatch = useDispatch();
  const SCROLL_DELAY_MS = 1000;

  /**
   * Function to set height of interaction post
   * @param event -any
   * @example isEmail
   */
  const formatMsgTime = () => {
    const date = new Date();
    const result = date.toUTCString();
    return result;
  };

  /**
   * Function to set height of interaction post
   * @param event -any
   * @example isEmail
   */
  const delay = (ms: any) => new Promise((resolve) => setTimeout(resolve, ms));

  /**
   * Handles the space key event for the copilot functionality based on the active contact's media type.
   * If the active contact's media is 'Voice':
   * - If the space key is pressed and there are more copilot voice assist data available, and the search input is not focused,
   *   it hides the NBR block, sets the request processing state, and after a delay of 2 seconds, sets the request processing state to false
   *   and calls the handleCopilotResponse function.
   * - If there are no more copilot voice assist data available, it calls the handleDispositionCard function.
   *
   * If the active contact's media is 'Email' and channel is 'Email':
   * - If the space key is pressed and there are more copilot email assist data available, and the search input is not focused,
   *   it hides the NBR block, sets the request processing state, and after a delay of 2 seconds, sets the request processing state to false.
   *   If the space button is clicked for the second time, it sets the isCopilotEmailResponseGenerated state to true.
   *   Finally, it calls the handleCopilotResponse function.
   * - If there are no more copilot email assist data available, it calls the handleDispositionCard function.
   *
   * @param event - The key event object.
   */
  const spaceFunction = useCallback(
    (event?: any) => {
      if (
        activeContact?.media === MediaType.DIGITAL &&
        activeContact?.channel === DigitalChannelType.CHAT &&
        activeContact?.copilotAssist === 'Yes' &&
        activeContact?.copilotSource === 'JSON'
      ) {
        if (
          event?.key === 'ArrowRight' &&
          flowMsgIndex < copilotFlowJson.length &&
          document.getElementById('searchInput')?.id !==
            document.activeElement?.id
        ) {
          if (isAssetsAddedToCopilotSuggestion) {
            dispatch(setIsAssetsAddedToCopilotSuggestion(false));
          }
          if (rtSummary === null || rtSummary === undefined) {
            const response = {
              agentAssistSource: 'Illuminate',
              contactId: `${activeContact?.id}`,
              utcTimestamp: new Date().toISOString(),
              acpAppElements: [
                {
                  cardType: 'Text',
                  contentType: 'rtSummary',
                  // eslint-disable-next-line no-useless-escape
                  content: JSON.stringify({
                    title: 'rtSummary',
                    summary: activeContact?.rtSummary,
                  }),
                  objectId: 'e15d4e75-39fa-4f9d-85a1-daffe6d16a40_rtSummary',
                },
              ],
              type: null,
            } as unknown as ApiResponse;
            processApiResponse(response, dispatch);
          }
          if (
            suggestionsCardList &&
            suggestionsCardList.messageFrom !== 'copilot'
          ) {
            processCustomerResponse(
              suggestionsCardList.message,
              suggestionsCardList?.messageFrom
            );
            handleScrollToBottom();
          } else if (
            suggestionsCardList &&
            suggestionsCardList.messageFrom === 'copilot' &&
            flowMsgIndex > 0
          ) {
            dispatch(setIsRequestProcessing(true));
            setTimeout(() => {
              dispatch(setIsRequestProcessing(false));
              handleCopilotResponse();
            }, 2000);
          }
          dispatch(updateFlowMsgIndex(flowMsgIndex + 1));
        } else if (copilotFlowJson.length === flowMsgIndex) {
          handleDispositionCard();
          dispatch(updateFlowMsgIndex(flowMsgIndex + 1));
        }
      }
    },
    [flowMsgIndex]
  );

  useEffect(() => {
    document.addEventListener('keydown', spaceFunction, false);

    return () => {
      document.removeEventListener('keydown', spaceFunction, false);
    };
  }, [spaceFunction]);

  /**
   * Function to scroll to bottom of the chat window
   * @param event -any
   * @example handleScrollToBottom
   */
  const handleScrollToBottom = async () => {
    await delay(SCROLL_DELAY_MS);
    document.getElementById('bodyContainer')?.scrollTo({
      top: document.getElementById('bodyContainer')?.scrollHeight,
      behavior: 'smooth',
    });
  };

  /**
   * Function to render copilot response in copilot block
   * @param event -any
   * @example handleCopilotResponse
   */
  const handleCopilotResponse = async () => {
    dispatch(updateAppSpaceTab({ tab: AppSpace.COPILOT }));
    renderCopilotCards(suggestionsCardList);
    // if (
    //   suggestionsCardList?.recordType === 'sentiment' ||
    //   suggestionsCardList?.recordType === 'salesOffer' ||
    //   suggestionsCardList?.recordType === 'behavioralGuidance'
    // ) {
    //   await delay(COPILOT_RESPONSE_DELAY_MS);
    //   dispatch(updateFlowMsgIndex(flowMsgIndex + 1));
    // }
    //dispatch(setIsRequestProcessing(false));
  };

  /**
   * Function to open disposition card
   * @param event -any
   * @example handleDispositionCard
   */
  const handleDispositionCard = async () => {
    if (!isDispositionOpen) {
      await delay(SCROLL_DELAY_MS);
      dispatch(toggleDispositionCard(true));
    }
  };

  /**
   * returnAgentCopilotContentAction - retun agent copilot content action based on record type
   * @param props - suggestion
   * @example returnAgentCopilotContentAction()
   */
  const returnAgentCopilotContentAction = (recordType: string) => {
    const actionMap: { [key: string]: string } = {
      sentiment: 'sentimentAndReason',
      suggestionArticle: 'kbCombo',
      salesOffer: 'salesOffer',
      behavioralGuidance: 'behavioralGuidance',
      intentAndAssistance: 'intentAndAssistance',
      // Default case is handled by returning 'compliance' if recordType is not found in actionMap
    };

    return actionMap[recordType] || 'compliance';
  };

  /**
   * renderCopilotCards - render copilot cards in copilot block
   * @param props -?-suggestion
   * @example renderCopilotCards()
   */
  const renderCopilotCards = (suggestionsCardList: any) => {
    const { recordType, sentimentScore, message } = suggestionsCardList;
    const agentCopilotContentAction =
      returnAgentCopilotContentAction(recordType);
    const isSentimentAndReason =
      agentCopilotContentAction === 'sentimentAndReason';
    const contactId = `${activeContact?.id}`;
    const utcTimestamp = isSentimentAndReason
      ? new Date().toISOString()
      : '2024-04-23T04:27:23.455584Z';
    const objectIdPrefix = 'e15d4e75-39fa-4f9d-85a1-daffe6d16a40_';

    let content;
    const contentType = agentCopilotContentAction;
    let objectId = objectIdPrefix + contentType;

    if (isSentimentAndReason) {
      const sentimentLabels = ['Negative', 'Neutral', 'Positive'];
      const sentimentIndex = Math.max(
        0,
        Math.min(sentimentScore, sentimentLabels.length - 1)
      );
      const sentimentString = sentimentLabels[sentimentIndex];
      const reason = message[0].description;

      const sentimentMap: {
        [key: string]: { sentiment: string; icon: string };
      } = {
        Neutral: {
          sentiment: translate('CustomerSentimentisNeutral'),
          icon: 'NeutralPositiveSentimentIcon',
        },
        Positive: {
          sentiment: translate('CustomerSentimentisPositive'),
          icon: 'PositiveSentimentIcon',
        },
        Negative: {
          sentiment: translate('CustomerSentimentisNegative'),
          icon: 'NegativeSentimentIcon',
        },
      };

      const { sentiment, icon } = sentimentMap[sentimentString];
      content = JSON.stringify({ sentiment, icon, description: reason });
      objectId = objectIdPrefix + 'sentiment';
    } else if (agentCopilotContentAction === 'behavioralGuidance') {
      handleBehavioralGuidance(message[0]);
    } else if (agentCopilotContentAction === 'intentAndAssistance') {
      dispatch(
        updateContactIntents({ id: activeContact?.id, intentsSet: message })
      );
      handleIntent(message);
    } else {
      content =
        recordType === 'compliance'
          ? JSON.stringify({
              formattedInstructionsForAgent: `${message[0].description}`,
            })
          : recordType === 'salesOffer'
          ? JSON.stringify({ offers: message[0] })
          : JSON.stringify(message[0]);
    }

    const response = {
      agentAssistSource: 'Illuminate',
      contactId,
      utcTimestamp,
      acpAppElements: [
        {
          cardType: 'AdaptiveCard',
          contentType,
          content,
          objectId,
        },
      ],
      type: null,
    } as ApiResponse;

    processApiResponse(response, dispatch);
  };

  /**
   * processCustomerResponse - process customer response in chat text editor to next state
   * @param props -?-suggestion
   * @example processCustomerResponse()
   */
  const processCustomerResponse = (message: any, source: string) => {
    const replyMessage = {
      messageId: new Date().toString(),
      postId: 'chat_b8f6e4f2_a8af_4607_b7e8_e3b5d748b09d',
      isOwn: true,
      createdAt: formatMsgTime(),
      messageContent: {
        payload: {
          text: source === 'agent' ? message[0] : message,
        },
      },
      reactionStatistics: {
        likes: 0,
      },
      authorUser: {
        firstName:
          source === 'customer' ? activeContact?.customer?.firstName : '',
        surname: source === 'customer' ? activeContact?.customer?.lastName : '',
        emailAddress:
          source === 'customer'
            ? activeContact?.customer?.emailId
            : 'customer.service@delta.com',
        loginUsername:
          source === 'customer' ? activeContact?.customer?.twitterId : '',
      },
      attachments: [],
      conferenceMsg: false,
    };
    // const replyMessage = {tweet: message, id: new Date().toString(), name: 'Agent'}
    dispatch(
      updateTwitterAgentReply({
        interactionId: activeInteractionID,
        replyMessage,
      })
    );

    // if (
    //   !suggestionsCardList?.message[0].includes(
    //     'thinking of going in late June'
    //   )
    // ) {
    //   setTimeout(() => {
    //     dispatch(updateFlowMsgIndex(flowMsgIndex + 1));
    //   }, 3000);
    // }
  };

  /**
   * Function to handle user reply
   * @example - handleIntent()
   * @returns
   */
  const handleIntent = (intents: any) => {
    const intentTitle = intents[0]?.title ?? 'no matching intent title found';
    const intentText = intents[0]?.intent ?? 'no matching intent found';
    const tasks = intents[0]?.tasks ?? [];
    const outputPayload = {
      intent: intentText,
      title: intentTitle,
      tasks: [],
    };
    interface TaskData {
      order: any;
      text: any;
      choices?: { title: string; value: string }[];
    }
    const currentDate = new Date().toLocaleDateString();
    const modifiedTasks = tasks?.map((task: any) => {
      const taskData = {
        order: task.order,
        text: task.text.replace('%%date%%', currentDate),
      };
      if (task.buttons) {
        const choices = task.buttons.map((button: any) => {
          return { title: button.text, value: button.text };
        });
        return { ...taskData, choices };
      }
      return taskData;
    });

    // Assign modified tasks array to output payload
    outputPayload.tasks = modifiedTasks;

    const intentToBeDisplayed = `It looks like ${activeContact?.customer?.firstName} would like to ${intentTitle}.`;
    const filteredTasks = outputPayload?.tasks?.filter(
      (task: any) => task.choices
    );
    filteredTasks.forEach((task: any, index: any) => {
      task.changedOrder = index + 1;
    });
    const responseContent = {
      title: `${translate('taskAssist')}: ${intentTitle}`,
      intent: intentToBeDisplayed,
      intentSubTitle: intentTitle,
      tasks: filteredTasks,
    };
    const response = {
      agentAssistSource: 'Illuminate',
      contactId: `${activeContact?.id}`,
      utcTimestamp: '2024-04-23T04:27:23.455584Z',
      acpAppElements: [
        {
          cardType: 'AdaptiveCard',
          contentType: 'intentAndAssistance',
          // eslint-disable-next-line no-useless-escape
          //"content":"{\n  \"icon\": \"PositiveSentimentIcon\",\n  \"sentiment\": \"Customer Sentiment is Positive\",\n  \"description\": \"The agent showed empathy, offered a solution, and ensured the well-being of the customer's child.\"\n}"
          content: JSON.stringify(responseContent),
          objectId: 'e15d4e75-39fa-4f9d-85a1-daffe6d16a40_intent',
        },
      ],
      type: null,
    } as ApiResponse;
    processApiResponse(response, dispatch);
    return;
  };

  /**
   * Function to handle bhavioral guidance
   * @example - handleBehavioralGuidance()
   * @returns
   */
  const handleBehavioralGuidance = (userReply: string) => {
    const copilotReply = userReply || '';
    // eslint-disable-next-line @nice-cxone/ccf/required-tsdoc
    const generateResponseContent = (copilotReply: string) => {
      let title, icon;
      if (copilotReply.includes('Enlighten Alert')) {
        title = translate('CopilotAlert');
        icon =
          'data:image/png;base64,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';
      } else if (
        copilotReply.includes('Kudos') ||
        copilotReply.includes('Nice work!') ||
        copilotReply.includes('Great job')
      ) {
        title = translate('AgentPraise');
        icon =
          'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik01LjAwMDAzIDcuNUM1LjAwMDAzIDguODI2MDggNS41MjY4MSAxMC4wOTc5IDYuNDY0NSAxMS4wMzU1QzcuNDAyMTggMTEuOTczMiA4LjY3Mzk1IDEyLjUgMTAgMTIuNUMxMS4zMjYxIDEyLjUgMTIuNTk3OSAxMS45NzMyIDEzLjUzNTYgMTEuMDM1NUMxNC40NzMyIDEwLjA5NzkgMTUgOC44MjYwOCAxNSA3LjVDMTUgNi4xNzM5MiAxNC40NzMyIDQuOTAyMTUgMTMuNTM1NiAzLjk2NDQ3QzEyLjU5NzkgMy4wMjY3OCAxMS4zMjYxIDIuNSAxMCAyLjVDOC42NzM5NSAyLjUgNy40MDIxOCAzLjAyNjc4IDYuNDY0NSAzLjk2NDQ3QzUuNTI2ODEgNC45MDIxNSA1LjAwMDAzIDYuMTczOTIgNS4wMDAwMyA3LjVaIiBzdHJva2U9IiMwMDg5NDAiIHN0cm9rZS13aWR0aD0iMS42NjY2NyIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+DQo8cGF0aCBkPSJNMTAgMTIuNUwxMi44MzM0IDE3LjQwODNMMTQuMTY1IDE0LjcxNDJMMTcuMTYzNCAxNC45MDc1TDE0LjMzIDEwTTUuNjY4MzYgMTBMMi44MzUwMiAxNC45MDgzTDUuODMzMzYgMTQuNzE0Mkw3LjE2NTAyIDE3LjQwNzVMOS45OTgzNiAxMi41IiBzdHJva2U9IiMwMDg5NDAiIHN0cm9rZS13aWR0aD0iMS42NjY2NyIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+DQo8L3N2Zz4NCg==';
      } else {
        title = translate('CopilotInsight');
        icon =
          'data:image/svg+xml;base64,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';
      }

      const responseContent = {
        title: title,
        icon: icon,
        copilotReply:
          (copilotReply && typeof copilotReply === 'string'
            ? copilotReply
                ?.replace(/<\/?[^>]+(>|$)/g, '')
                ?.split(':')
                ?.pop()
                ?.trim()
            : '') || '',
      };

      return responseContent;
    };

    const responseContent = generateResponseContent(copilotReply);
    const response = {
      agentAssistSource: 'Illuminate',
      contactId: `${activeContact?.id}`,
      utcTimestamp: new Date().toISOString(),
      acpAppElements: [
        {
          cardType: 'AdaptiveCard',
          contentType: 'behavioralGuidance',
          content: JSON.stringify(responseContent),
          objectId: 'e15d4e75-39fa-4f9d-85a1-daffe6d16a40_behavioralGuidance',
        },
      ],
      type: null,
    } as ApiResponse;

    processApiResponse(response, dispatch);
    return;
  };

  return (
    <CcfCopilotNBR
      sliderItemsArr={
        suggestionsCardList?.messageFrom === 'agent'
          ? suggestionsCardList?.message
          : []
      }
    />
  );
}
